import * as THREE from 'three';
import { localHeadline } from '../content/headlines';
import type { SfxName } from '../content/types';
import { clamp } from '../core/math';
import type { Player } from '../entities/Player';
import { hourOf, minutesUntilWake } from '../state/rules';
import type { Stage, StageButton, StageHud } from '../stages/types';
import type { Interactions } from '../systems/Interactions';
import { backFortyDepth, SPOTS, type SpotId } from '../world/layout';
import { Ufo } from '../world/Ufo';
import { buildUfoInterior, UFO_SPOTS, type Saucer } from '../world/ufoRoom';
import type { Activities } from './Activities';
import type { Interiors } from './Interiors';
import type { RestCycle } from './RestCycle';
import type { GameServices } from './services';

/** Seconds between rolls for a visit on a dark night. */
const CHECK_EVERY = 10;
/** Chance per roll, before anything makes it likelier. */
const BASE_CHANCE = 0.005;

/** Where they drop you off, and what you wake up to. */
const DROPS: ReadonlyArray<{ spot: SpotId; text: string }> = [
  {
    spot: 'mudFinish',
    text: 'You wake up face down in the middle of the mud bog. One Croc is missing. The other one is on your head.',
  },
  {
    spot: 'earlFirePit',
    text: "You wake up in Earl's lawn chair with a perfect circle shaved into your mullet. Earl says he didn't see nothing. Earl saw everything.",
  },
  {
    spot: 'junkyardDrop',
    text: "You wake up on the roof of a crushed Buick in Jerry's yard, holding a pamphlet you can't read. It's very well designed.",
  },
  {
    spot: 'rodeoChute',
    text: 'You wake up in the bucking chute next to Widowmaker. He looks at you like he knows. He knows.',
  },
  {
    spot: 'picnicTable',
    text: 'You wake up on the picnic table at the Commons, arms folded over your chest like a vampire. Ricky took a picture. It has 400 likes.',
  },
  {
    spot: 'bmxField',
    text: "You wake up in the middle of the BMX field. The grass around you is flattened in a perfect circle. Skeeter's already charging admission.",
  },
  {
    spot: 'gunCounter',
    text: 'You wake up behind the counter at the gun range. Gus is pointing a broom at you. "Explain." You cannot.',
  },
];

/** The cinematic: lights in the sky, a beam, and up you go. */
class BeamUp implements Stage {
  readonly title = '???';
  readonly crosshair = false;
  readonly pointerLock = false;
  private t = 0;
  private readonly start: THREE.Vector3;
  private readonly hover: THREE.Vector3;
  private readonly look = new THREE.Vector3();
  private hum = 0;

  constructor(
    private readonly player: Player,
    private readonly ufo: Ufo,
    private readonly sfx: (n: SfxName) => void,
  ) {
    const p = player.position;
    this.hover = new THREE.Vector3(p.x, 17, p.z);
    this.start = new THREE.Vector3(p.x - 40, 80, p.z - 30);
  }

  get done(): boolean {
    return this.t > 11.5;
  }

  get whiteout(): number {
    return clamp((this.t - 9.8) / 1.2, 0, 1);
  }

  hint(): string {
    return this.t < 5 ? '...what is that light?' : 'Your feet are leaving the ground.';
  }

  buttons(): StageButton[] {
    return [];
  }

  begin(): void {
    this.player.scripted = true;
    this.ufo.show(this.start);
    this.sfx('ufo');
  }

  update(dt: number): void {
    this.t += dt;
    const t = this.t;
    this.hum -= dt;
    if (this.hum <= 0 && t > 4) {
      this.hum = 4.8;
      this.sfx('ufo');
    }
    // In from the dark, slow at the end.
    const k = clamp(t / 5, 0, 1);
    const ease = 1 - (1 - k) ** 3;
    this.ufo.group.position.lerpVectors(this.start, this.hover, ease);
    this.ufo.beamOn = clamp((t - 4.6) / 0.8, 0, 1);
    const p = this.player;
    // Up you go, slowly turning, arms out.
    const lift = clamp((t - 5.6) / 5, 0, 1);
    p.y = lift * lift * 15;
    p.facing += dt * (0.5 + lift * 2);
    p.anim.update({ dt, time: t, speed: 0, pose: lift > 0 ? 'fallen' : 'wave' });
    p.syncTransform();
  }

  frame(camera: THREE.PerspectiveCamera): void {
    const p = this.player.position;
    // On the ground beside you, craning up: at the lights, then at you going up into them.
    camera.position.set(p.x + 5.5, 1.4, p.z + 5.5);
    if (this.t < 5.6) camera.lookAt(this.ufo.group.position);
    else camera.lookAt(this.look.set(p.x, this.player.y + 1.5, p.z));
  }

  hud(): StageHud {
    return {
      banner: this.t > 6.5 && this.t < 9.5 ? { text: 'NOPE NOPE NOPE', color: '#9dff6a' } : null,
    };
  }

  end(): void {
    this.player.scripted = false;
    this.player.y = 0;
  }
}

/**
 * Every so often, late on a dark night, something comes for somebody. Out in the Back
 * Forty it's likelier; in Hank's tinfoil hat, likelier still. They take you up, show
 * you around, and drop you off somewhere embarrassing with a few hours missing.
 */
export class Abduction {
  readonly ufo: Ufo;
  readonly saucer: Saucer;
  private check = CHECK_EVERY;
  /** Another player's abduction we're watching from the ground. */
  private watching: { at: THREE.Vector3; until: number } | null = null;
  private took = false;

  constructor(
    private readonly s: GameServices,
    private readonly interiors: Interiors,
    private readonly act: Activities,
    private readonly rest: RestCycle,
  ) {
    this.ufo = new Ufo(s.world.dynamic);
    this.saucer = buildUfoInterior(s.world.dynamic, s.world.collision);
    // There's no door: you leave on the button. This is just where Interiors thinks it is.
    interiors.addPublic(this.saucer.room, SPOTS.bmxField);
  }

  /** How likely a visit is right now (0 when it can't happen at all). */
  private odds(): number {
    const { store, player } = this.s;
    const h = hourOf(store.minutes);
    if (!(h >= 23 || h < 3)) return 0;
    if (store.flag('ufo_day') === store.day) return 0;
    let p = BASE_CHANCE;
    if (backFortyDepth(player.position.x, player.position.z) > 0.5) p *= 3;
    if (store.flag('foil_on') && store.has('foilHat')) p *= 4;
    if (store.flag('ufo_omen') === store.day || store.flag('ufo_omen') === store.day - 1) p *= 6;
    if (store.stat('buzz') > 70) p *= 1.5;
    return p;
  }

  /** Per frame (while you're free to move). */
  update(dt: number, free: boolean): void {
    const now = this.s.now();
    if (this.watching) {
      const w = this.watching;
      this.ufo.show(w.at);
      this.ufo.beamOn = now < w.until - 2 ? 1 : Math.max(0, (w.until - now) / 2);
      if (now > w.until) {
        this.watching = null;
        this.ufo.show(null);
      }
      return;
    }
    if (!free || this.interiors.inside || this.s.player.busy) return;
    this.check -= dt;
    if (this.check > 0) return;
    this.check = CHECK_EVERY;
    if (this.s.rng.chance(this.odds())) this.begin();
  }

  /** Somebody else in the park is getting taken: you see it from the ground. */
  watch(x: number, z: number): void {
    this.watching = { at: new THREE.Vector3(x, 17, z), until: this.s.now() + 11 };
    this.s.sfx('ufo');
    this.s.store.toast('Lights over the park... somebody just got BEAMED UP.', 'meme', ':ufo:');
  }

  /** Here they come. */
  begin(): void {
    const { store, ui, player } = this.s;
    store.setFlag('ufo_day', store.day);
    this.took = false;
    this.s.share({ kind: 'ufo', x: player.position.x, z: player.position.z });
    ui.stages.open(new BeamUp(player, this.ufo, (n) => this.s.sfx(n)), () => {
      this.ufo.show(null);
      this.ufo.beamOn = 0;
      store.incr('abductions');
      const e = this.saucer.room.entry;
      player.teleport(e.x, e.z, Math.PI);
      this.s.cameraRig.snap();
      store.toast('You are on a spaceship. You are ON. A. SPACESHIP.', 'meme', ':alien:');
      return null;
    });
  }

  register(interactions: Interactions): void {
    const { store, ui, rng } = this.s;
    const add = interactions.add.bind(interactions);
    const room = this.saucer.room;
    const at =
      (p: { x: number; z: number }, dx = 0, dz = 0) =>
      () =>
        room.world({ x: p.x + dx, z: p.z + dz });
    add({
      id: 'ufo-table',
      at: at(UFO_SPOTS.table, 0, 1.0),
      radius: 1.3,
      prompt: () => (store.doneToday('ufo_exam') ? 'The table is still warm' : 'Lie down on the exam table'),
      action: () => this.exam(),
      icon: ':bed:',
      highlight: () => this.saucer.table,
    });
    add({
      id: 'ufo-aliens',
      at: at(UFO_SPOTS.aliens, -0.4, 0.9),
      radius: 1.6,
      prompt: () => 'Talk to the visitors',
      action: () => this.talk(),
      icon: ':alien:',
      highlight: () => this.saucer.aliens[1]?.group ?? null,
    });
    add({
      id: 'ufo-cow',
      at: at(UFO_SPOTS.cow, 1.6, 1.2),
      radius: 1.3,
      prompt: () => 'Knock on the cow tube',
      action: () => {
        this.s.sfx('moo');
        store.toast(
          rng.pick([
            'The cow looks at you. You look at the cow. You are both a long way from home.',
            'MOOOOO. Same, cow. Same.',
          ]),
          'meme',
          ':cow:',
        );
      },
      icon: ':cow:',
      highlight: () => this.saucer.cow,
    });
    add({
      id: 'ufo-jars',
      at: at(UFO_SPOTS.jars, -1.0, 1.2),
      radius: 1.5,
      prompt: () => 'Look at the specimen jars',
      action: () =>
        ui.modal.show({
          title: 'Specimens',
          art: ':specimen-jar:',
          text: "Jar one: a possum, floating, looking extremely calm about it. Jar two: a lawn gnome they clearly think is a person. Jar three: a Nattie Lite the size of a dog, labeled with a symbol that you're pretty sure means 'FAVORITE.'",
          choices: [],
          leave: 'Huh',
        }),
      icon: ':specimen-jar:',
    });
    add({
      id: 'ufo-gizmo',
      at: at(UFO_SPOTS.gizmo, -0.8, 0.4),
      radius: 1.2,
      prompt: () => (this.took ? null : 'Pocket the glowing doohickey'),
      action: () => {
        this.took = true;
        store.give('alienGizmo', 1);
        this.saucer.gizmo.visible = false;
        this.s.sfx('zap');
        store.toast('It hums in your pocket. Nobody saw. Probably. They see everything.', 'meme', ':ufo:');
      },
      icon: ':sparkles:',
      highlight: () => this.saucer.gizmo,
    });
    add({
      id: 'ufo-button',
      at: at(UFO_SPOTS.button, 0.4, 0.6),
      radius: 1.2,
      prompt: () => 'Press the big red button',
      action: () => this.beamDown(),
      priority: 1,
      icon: ':red-button:',
      highlight: () => this.saucer.button,
    });
  }

  private exam(): void {
    const { store, ui, player } = this.s;
    if (!this.act.canAct() || store.doneToday('ufo_exam')) return;
    store.markToday('ufo_exam');
    const t = this.saucer.room.world(UFO_SPOTS.table);
    player.teleport(t.x, t.z, Math.PI / 2);
    player.y = 0.62;
    player.doBusy({
      pose: 'fallen',
      until: this.s.now() + 3,
      facing: Math.PI / 2,
      onDone: () => {
        player.y = 0;
        player.teleport(t.x, t.z + 1.1, 0);
        store.unlock('probed');
        store.apply({ stats: { health: -5, energy: 15 }, clout: 6 });
        ui.modal.show({
          title: 'The Exam',
          art: ':alien:',
          text: "Cold hands. Colder instruments. A long beep, a short beep, and one of them writes something down and underlines it twice. Another one holds up your Crocs to the light for a very long time. When it's over you feel... calibrated. You'd give it four stars. The lighting was harsh.",
          choices: [],
          leave: 'Ow',
        });
      },
    });
  }

  private talk(): void {
    const { ui, rng } = this.s;
    ui.modal.show({
      title: 'The Visitors',
      art: ':alien:',
      text: "Three big heads turn toward you at the same time. In your head, in a voice like a dial-up modem, you hear: 'GREETINGS, BUBBA.' They know your name. They know all the names.",
      choices: [
        {
          label: 'Take me to your leader',
          run: () =>
            "They point, all three at once, at a picture on the wall. It's Darlene. It's a picture of Darlene. You have so many questions.",
        },
        {
          label: 'Offer them a Nattie Lite',
          hint: '-1 :beer:',
          requires: ({ store }) => store.has('beer') || 'No beer on you',
          run: ({ store }) => {
            store.take('beer');
            return {
              text: 'The tallest one takes it in two long fingers, cracks it, and holds it up to the dome light like a sacred relic. Then it shotguns it. The other two lose their minds. You are, for the moment, a god.',
              clout: 10,
            };
          },
        },
        {
          label: 'Ask about the Camaro',
          run: () =>
            rng.pick([
              "'WE HAVE SEEN IT. FROM ORBIT.' A pause. 'IT WILL NOT RUN.'",
              "'THE CAMARO IS A RITE. WE RESPECT THE RITE.' They do not elaborate.",
            ]),
        },
        {
          label: 'Ask what they want',
          run: () =>
            "'COWS,' says one. 'CABLE,' says another. The third one just points at your mullet and nods slowly.",
        },
      ],
      leave: 'Back away slowly',
    });
  }

  /** The button: a flash, missing time, and a soft landing somewhere you'll have to explain. */
  private beamDown(): void {
    const { store, ui, rng, player } = this.s;
    if (this.rest.sleeping) return;
    this.rest.sleeping = true;
    this.s.sfx('zap');
    ui.hud.showFade(':ufo:', 'Beaming you down...');
    window.setTimeout(() => {
      const drop = rng.pick(DROPS);
      const p = SPOTS[drop.spot];
      // Missing time: you come to around dawn, rested if a little rattled.
      this.rest.skip(minutesUntilWake(store.minutes) + rng.int(10, 50));
      this.interiors.addPublic(this.saucer.room, p);
      this.interiors.leave();
      player.teleport(p.x, p.z, 0);
      this.s.cameraRig.snap();
      this.saucer.gizmo.visible = true;
      store.setFlag('ufo_unreported', 1);
      store.headline(localHeadline('ufo', store.state.player.name));
      this.s.save();
      window.setTimeout(() => {
        ui.hud.hideFade();
        this.rest.sleeping = false;
        this.s.onUiClosed();
        ui.modal.show({
          title: 'Where... Am I?',
          art: ':ufo:',
          text: `${drop.text}\n\nIt's morning. A few hours are missing. Hank on Lot 15 is going to want to hear about this.`,
          choices: [],
          leave: 'Act natural',
        });
        store.apply({ clout: 5 });
      }, 900);
    }, 1600);
  }
}
